/**
 * Indian cities / areas for marketplace location autocomplete.
 * Labels are "City, State" (or "Area, City, State") so dropdown + bound value show both.
 * Coordinates are locality centres used for search radius matching.
 */

/** Build display label: City, State (optional Area prefix). */
export function formatCityStateLabel(area, city, state) {
  const a = String(area || "").trim();
  const c = String(city || "").trim();
  const s = String(state || "").trim();
  const cityState = c && s ? `${c}, ${s}` : c || s || "";
  if (a && cityState && a.toLowerCase() !== c.toLowerCase()) return `${a}, ${cityState}`;
  return cityState || a || "";
}

/** @deprecated Prefer formatCityStateLabel — kept for callers that only pass area/city. */
export function formatAreaCityLabel(area, city, state = "") {
  return formatCityStateLabel(area, city, state);
}

/**
 * From reverse-geocode / place parts → marketplace filter label.
 * Prefers locality/suburb as area bound with city + state.
 */
export function formatExactLocationLabel({
  locality = "",
  suburb = "",
  neighbourhood = "",
  area = "",
  city = "",
  town = "",
  village = "",
  state = "",
} = {}) {
  const areaPart =
    String(area || locality || suburb || neighbourhood || "").trim() || "";
  const cityPart = String(city || town || village || "").trim() || "";
  const statePart = String(state || "").trim() || "";
  return formatCityStateLabel(areaPart, cityPart, statePart);
}

function entry({ area = "", city, latitude, longitude, state = "" }) {
  const label = formatCityStateLabel(area, city, state);
  return {
    label,
    area: String(area || "").trim() || null,
    city: String(city || "").trim(),
    state: String(state || "").trim() || null,
    latitude,
    longitude,
  };
}

export const INDIAN_CITIES = [
  // —— Metro city centres (City, State label) ——
  entry({ city: "Mumbai", state: "Maharashtra", latitude: 19.07609, longitude: 72.877426 }),
  entry({ city: "Delhi", state: "NCR", latitude: 28.613939, longitude: 77.209023 }),
  entry({ city: "Bengaluru", state: "Karnataka", latitude: 12.9716, longitude: 77.5946 }),
  entry({ city: "Hyderabad", state: "Telangana", latitude: 17.385044, longitude: 78.486671 }),
  entry({ city: "Chennai", state: "Tamil Nadu", latitude: 13.08268, longitude: 80.270718 }),
  entry({ city: "Kolkata", state: "West Bengal", latitude: 22.572646, longitude: 88.363895 }),
  entry({ city: "Pune", state: "Maharashtra", latitude: 18.52043, longitude: 73.856743 }),
  entry({ city: "Ahmedabad", state: "Gujarat", latitude: 23.022505, longitude: 72.571362 }),
  entry({ city: "Jaipur", state: "Rajasthan", latitude: 26.912434, longitude: 75.78727 }),
  entry({ city: "Lucknow", state: "Uttar Pradesh", latitude: 26.846694, longitude: 80.946166 }),
  entry({ city: "Chandigarh", latitude: 30.733315, longitude: 76.779419 }),
  entry({ city: "Kochi", state: "Kerala", latitude: 9.931233, longitude: 76.267303 }),
  entry({ city: "Indore", state: "Madhya Pradesh", latitude: 22.719568, longitude: 75.857727 }),
  entry({ city: "Nagpur", state: "Maharashtra", latitude: 21.1458, longitude: 79.088155 }),
  entry({ city: "Coimbatore", state: "Tamil Nadu", latitude: 11.016844, longitude: 76.955832 }),
  entry({ city: "Thane", state: "Maharashtra", latitude: 19.21833, longitude: 72.978088 }),
  entry({ city: "Navi Mumbai", state: "Maharashtra", latitude: 19.033, longitude: 73.0297 }),
  entry({ city: "Gurugram", state: "Haryana", latitude: 28.459496, longitude: 77.026638 }),
  entry({ city: "Faridabad", state: "Haryana", latitude: 28.408912, longitude: 77.317789 }),
  entry({ city: "Panipat", state: "Haryana", latitude: 29.390946, longitude: 76.963501 }),
  entry({ city: "Noida", state: "Uttar Pradesh", latitude: 28.535516, longitude: 77.391026 }),

  // —— Bengaluru areas (exact locality + city) ——
  entry({ area: "Indiranagar", city: "Bengaluru", state: "Karnataka", latitude: 12.9784, longitude: 77.6408 }),
  entry({ area: "Koramangala", city: "Bengaluru", state: "Karnataka", latitude: 12.9352, longitude: 77.6245 }),
  entry({ area: "Malleshwaram", city: "Bengaluru", state: "Karnataka", latitude: 13.0035, longitude: 77.5646 }),
  entry({ area: "Electronic City", city: "Bengaluru", state: "Karnataka", latitude: 12.8399, longitude: 77.677 }),
  entry({ area: "Whitefield", city: "Bengaluru", state: "Karnataka", latitude: 12.9698, longitude: 77.7499 }),
  entry({ area: "HSR Layout", city: "Bengaluru", state: "Karnataka", latitude: 12.9116, longitude: 77.6389 }),
  entry({ area: "Jayanagar", city: "Bengaluru", state: "Karnataka", latitude: 12.9308, longitude: 77.5838 }),
  entry({ area: "JP Nagar", city: "Bengaluru", state: "Karnataka", latitude: 12.9063, longitude: 77.5857 }),
  entry({ area: "J P Nagar", city: "Bengaluru", state: "Karnataka", latitude: 12.9063, longitude: 77.5857 }),
  entry({ area: "Marathahalli", city: "Bengaluru", state: "Karnataka", latitude: 12.9591, longitude: 77.6974 }),
  entry({ area: "Yelahanka", city: "Bengaluru", state: "Karnataka", latitude: 13.1005, longitude: 77.5963 }),
  entry({ area: "BTM Layout", city: "Bengaluru", state: "Karnataka", latitude: 12.9166, longitude: 77.6101 }),
  entry({ area: "Udaya Nagar", city: "Bengaluru", state: "Karnataka", latitude: 12.99165, longitude: 77.6716 }),
  entry({ area: "Uday Nagar", city: "Bengaluru", state: "Karnataka", latitude: 12.99165, longitude: 77.6716 }),
  entry({ area: "Mahadevapura", city: "Bengaluru", state: "Karnataka", latitude: 12.9911, longitude: 77.687 }),
  entry({ area: "A Narayanapura", city: "Bengaluru", state: "Karnataka", latitude: 12.9945, longitude: 77.6678 }),
  entry({ area: "KR Puram", city: "Bengaluru", state: "Karnataka", latitude: 13.0077, longitude: 77.695 }),
  entry({ area: "Ramamurthy Nagar", city: "Bengaluru", state: "Karnataka", latitude: 13.0124, longitude: 77.6773 }),

  // —— Mumbai areas ——
  entry({ area: "Andheri", city: "Mumbai", state: "Maharashtra", latitude: 19.1197, longitude: 72.8464 }),
  entry({ area: "Bandra", city: "Mumbai", state: "Maharashtra", latitude: 19.0596, longitude: 72.8295 }),
  entry({ area: "Powai", city: "Mumbai", state: "Maharashtra", latitude: 19.1176, longitude: 72.906 }),

  // —— Delhi / NCR areas ——
  entry({ area: "Connaught Place", city: "Delhi", state: "NCR", latitude: 28.6315, longitude: 77.2167 }),
  entry({ area: "Saket", city: "Delhi", state: "NCR", latitude: 28.5244, longitude: 77.2066 }),
  entry({ area: "Dwarka", city: "Delhi", state: "NCR", latitude: 28.5921, longitude: 77.046 }),
  entry({ area: "Sector 29", city: "Gurugram", state: "Haryana", latitude: 28.468, longitude: 77.064 }),
  entry({ area: "Sector 18", city: "Noida", state: "Uttar Pradesh", latitude: 28.5708, longitude: 77.326 }),

  // —— Andhra / nearby ——
  entry({ city: "Visakhapatnam", state: "Andhra Pradesh", latitude: 17.686816, longitude: 83.218482 }),
  entry({ city: "Vijayawada", state: "Andhra Pradesh", latitude: 16.506174, longitude: 80.648015 }),
  entry({ city: "Guntur", state: "Andhra Pradesh", latitude: 16.306652, longitude: 80.43654 }),
  entry({ city: "Tirupati", state: "Andhra Pradesh", latitude: 13.628756, longitude: 79.419179 }),
  entry({ city: "Nellore", state: "Andhra Pradesh", latitude: 14.442599, longitude: 79.986456 }),
  entry({ city: "Kakinada", state: "Andhra Pradesh", latitude: 16.989065, longitude: 82.247465 }),
  entry({ city: "Rajahmundry", state: "Andhra Pradesh", latitude: 17.000538, longitude: 81.804034 }),
  entry({ city: "Kurnool", state: "Andhra Pradesh", latitude: 15.828126, longitude: 78.037277 }),
  entry({ city: "Anantapur", state: "Andhra Pradesh", latitude: 14.681888, longitude: 77.600591 }),
  entry({ city: "Kadapa", state: "Andhra Pradesh", latitude: 14.467354, longitude: 78.824134 }),
  entry({ city: "Eluru", state: "Andhra Pradesh", latitude: 16.71066, longitude: 81.095243 }),
  entry({ city: "Ongole", state: "Andhra Pradesh", latitude: 15.505723, longitude: 80.049922 }),
  entry({ city: "Srikakulam", state: "Andhra Pradesh", latitude: 18.296944, longitude: 83.896944 }),
  entry({ city: "Vizianagaram", state: "Andhra Pradesh", latitude: 18.106658, longitude: 83.395554 }),
  entry({ city: "Machilipatnam", state: "Andhra Pradesh", latitude: 16.187468, longitude: 81.13888 }),
  entry({ city: "Amaravati", state: "Andhra Pradesh", latitude: 16.541649, longitude: 80.517029 }),
  entry({ city: "Proddatur", state: "Andhra Pradesh", latitude: 14.7502, longitude: 78.54813 }),
  entry({ city: "Nandyal", state: "Andhra Pradesh", latitude: 15.477995, longitude: 78.483605 }),
  entry({ city: "Warangal", state: "Telangana", latitude: 17.968901, longitude: 79.594054 }),
  entry({ city: "Karimnagar", state: "Telangana", latitude: 18.438555, longitude: 79.128861 }),
  entry({ city: "Madurai", state: "Tamil Nadu", latitude: 9.925201, longitude: 78.119774 }),
  entry({ city: "Salem", state: "Tamil Nadu", latitude: 11.664325, longitude: 78.146014 }),
  entry({ city: "Vellore", state: "Tamil Nadu", latitude: 12.916517, longitude: 79.132499 }),
];

/** Max distance (km) to snap GPS to a city/area centre label. */
export const NEAREST_CITY_MAX_KM = 55;

export function filterIndianCities(query, limit = 8) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return INDIAN_CITIES.slice(0, limit);
  return INDIAN_CITIES.filter((c) => {
    const hay = [c.label, c.area, c.city, c.state].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  }).slice(0, limit);
}

function normalizeCityAlias(name) {
  const n = String(name || "").trim().toLowerCase();
  if (n === "gurgaon") return "gurugram";
  if (n === "bangalore") return "bengaluru";
  if (n === "bombay") return "mumbai";
  if (n === "calcutta") return "kolkata";
  if (n === "madras") return "chennai";
  return n;
}

function cityNameMatches(catalogCity, queryCity) {
  const a = normalizeCityAlias(catalogCity);
  const b = normalizeCityAlias(queryCity);
  return Boolean(a && b && a === b);
}

export function resolveIndianCity(label) {
  const raw = String(label || "").trim().toLowerCase();
  if (!raw) return null;
  const stripped = raw.replace(/^near\s+me\s*[·•\-–]\s*/i, "").replace(/^near\s+/i, "").trim();

  const exact = INDIAN_CITIES.find((c) => c.label.toLowerCase() === stripped);
  if (exact) return exact;

  const parts = stripped.split(",").map((p) => p.trim()).filter(Boolean);

  // "Area, City, State" or "City, State" — prefer area match on first segment
  const areaPart = parts[0] || "";
  if (areaPart.length >= 3) {
    const byArea = INDIAN_CITIES.filter(
      (c) =>
        String(c.area || "").toLowerCase() === areaPart ||
        c.label.toLowerCase().startsWith(areaPart)
    );
    if (byArea.length === 1) return byArea[0];
  }

  // City-only typed value (or first segment when not an area)
  const exactCity = INDIAN_CITIES.find(
    (c) => !c.area && cityNameMatches(c.city, areaPart)
  );
  if (exactCity) return exactCity;

  // "City, State" or "Area, City, State" — resolve by city (+ optional state)
  if (parts.length >= 2) {
    const maybeState = parts[parts.length - 1];
    const maybeCity = parts.length >= 3 ? parts[parts.length - 2] : parts[0];
    const byCityState = INDIAN_CITIES.find((c) => {
      if (c.area) return false;
      if (!cityNameMatches(c.city, maybeCity)) return false;
      if (!c.state) return true;
      return String(c.state).toLowerCase() === maybeState;
    });
    if (byCityState) return byCityState;

    const byCityOnly = INDIAN_CITIES.find(
      (c) => !c.area && cityNameMatches(c.city, maybeCity)
    );
    if (byCityOnly) return byCityOnly;
  }

  if (areaPart.length >= 4) {
    const matches = INDIAN_CITIES.filter((c) => {
      const hay = [c.label, c.city, c.state, c.area].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(areaPart) || cityNameMatches(c.city, areaPart);
    });
    if (matches.length === 1) return matches[0];
    const cityCentre = matches.find((c) => !c.area && cityNameMatches(c.city, areaPart));
    if (cityCentre) return cityCentre;
  }
  return null;
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Closest known area/city for GPS. Keeps exact lat/lng; label is City, State (or Area, City, State).
 */
export function resolveNearestIndianCity(latitude, longitude, { maxKm = NEAREST_CITY_MAX_KM } = {}) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  let nearest = null;
  let nearestKm = Infinity;
  for (const city of INDIAN_CITIES) {
    const km = haversineKm(lat, lng, city.latitude, city.longitude);
    if (km < nearestKm) {
      nearestKm = km;
      nearest = city;
    }
  }
  if (!nearest) return null;
  const withinRange = !(Number.isFinite(maxKm) && nearestKm > maxKm);
  return {
    ...nearest,
    label: nearest.label,
    latitude: lat,
    longitude: lng,
    distanceKm: nearestKm,
    approximate: !withinRange,
    withinRange,
  };
}

/** Default marketplace search location (homepage + find caregivers). */
export const DEFAULT_MARKETPLACE_LOCATION =
  INDIAN_CITIES.find((c) => /gurugram|gurgaon/i.test(c.city) && !c.area) ||
  INDIAN_CITIES.find((c) => /gurugram|gurgaon/i.test(c.label)) ||
  INDIAN_CITIES[0];

function stateAliases(state) {
  const s = String(state || "").trim().toLowerCase();
  if (!s) return [];
  if (s === "delhi" || s === "ncr" || s === "delhi ncr") return ["delhi", "ncr"];
  return [s];
}

/** Unique city names for a selected Indian state (for onboarding city dropdown). */
export function getCitiesForState(state) {
  const aliases = stateAliases(state);
  if (!aliases.length) return [];
  const cities = new Set();
  for (const row of INDIAN_CITIES) {
    const rowState = String(row.state || "").trim().toLowerCase();
    if (!aliases.includes(rowState)) continue;
    const city = String(row.city || "").trim();
    if (city) cities.add(city);
  }
  return [...cities].sort((a, b) => a.localeCompare(b));
}
